/* filters
========================================================================== */
// eslint-disable-next-line import/no-extraneous-dependencies
import A11yDialog from 'a11y-dialog';
import { escapeHtml } from '../../utils';
export const initFiltersDialog = () => {
    const el = document.getElementById('filters-panel');
    if (!el)
        return;
    const dialog = new A11yDialog(el);
    dialog.on('show', () => {
        el.setAttribute('data-state', 'open');
    });
    dialog.on('hide', () => {
        el.setAttribute('data-state', 'closed');
    });
};
let selectedFilters = {
    s: [],
    c: [],
    t: []
};
// Extract the UUID(s) from the link's data attributes, supporting both single and multiple UUIDs
function getLinkUuid(link) {
    if (link.dataset.uuid)
        return link.dataset.uuid;
    if (link.dataset.uuids)
        return JSON.parse(link.dataset.uuids);
    return null;
}
// Add the UUID(s) from the selectedFilters object based on the link type, supporting both single and multiple UUIDs
function addToSelectedFilters(linkType, linkUuid) {
    if (Array.isArray(linkUuid)) {
        linkUuid.forEach((uuid) => {
            selectedFilters[linkType].push(uuid);
        });
    }
    else {
        selectedFilters[linkType].push(linkUuid);
    }
}
// Remove the UUID(s) from the selectedFilters object based on the link type, supporting both single and multiple UUIDs
function removeFromSelectedFilters(linkType, linkUuid) {
    if (Array.isArray(linkUuid)) {
        selectedFilters[linkType] = selectedFilters[linkType].filter((uuid) => !linkUuid.includes(uuid));
    }
    else {
        selectedFilters[linkType] = selectedFilters[linkType].filter((uuid) => uuid !== linkUuid);
    }
}
function setSelectedFilters(links) {
    links.forEach((link) => {
        const linkType = link.dataset.type;
        const linkUuid = getLinkUuid(link);
        if (link.classList.contains('is-selected') && linkUuid !== null) {
            addToSelectedFilters(linkType, linkUuid);
        }
    });
}
function selectFilter(e) {
    e.preventDefault();
    const link = e.currentTarget;
    const linkType = link.dataset.type;
    const linkUuid = getLinkUuid(link);
    if (linkUuid === null)
        return;
    if (link.classList.contains('is-selected')) {
        link.classList.remove('is-selected');
        link.removeAttribute('aria-current');
        removeFromSelectedFilters(linkType, linkUuid);
    }
    else {
        link.classList.add('is-selected');
        link.setAttribute('aria-current', 'true');
        addToSelectedFilters(linkType, linkUuid);
    }
}
// Check if at least one filter is active to decide whether to show the count badge or not
function hasActiveFilters() {
    return Object.keys(selectedFilters).some((type) => selectedFilters[type].length > 0);
}
// Set the count badge for a given filter type or the total badge, hiding it if the count is zero
function setBadge(badge, count) {
    if (count > 0) {
        badge.textContent = String(count);
        badge.hidden = false;
    }
    else {
        badge.textContent = '';
        badge.hidden = true;
    }
}
// Update the counts for each filter type and the total count on the filter button
function updateBadges(el) {
    let total = 0;
    Object.keys(selectedFilters).forEach((type) => {
        const badge = el.querySelector(`[data-filters-badge="${type}"]`);
        const count = el.querySelectorAll(`[data-filters-target="link"].is-selected[data-type="${type}"]`).length;
        total += count;
        if (badge)
            setBadge(badge, count);
    });
    document.querySelectorAll('[data-filters-badge="total"]').forEach((totalBadge) => {
        setBadge(totalBadge, total);
    });
}
// Rebuild the list of active filters based on the selected links and attach events to remove them
function updateActiveFilters(el, searchLink, wrapperEl) {
    const container = el.querySelector('[data-filters-target="active-filters"]');
    if (!container)
        return;
    const allSelectedLinks = el.querySelectorAll('[data-filters-target="link"].is-selected');
    const selectedLinks = Array.from(allSelectedLinks);
    container.innerHTML = selectedLinks
        .map((link, i) => {
        const label = escapeHtml(link.textContent?.trim() ?? '');
        const group = link.dataset.group ? escapeHtml(link.dataset.group) + ' · ' : '';
        return `<li class="shrink-0"><button type="button" class="filters__tag" data-link-index="${i}">${group}${label}</button></li>`;
    })
        .join('');
    container.hidden = selectedLinks.length === 0;
    container.onclick = (e) => {
        const tag = e.target.closest('[data-link-index]');
        if (!tag)
            return;
        const link = selectedLinks[Number(tag.dataset.linkIndex)];
        const linkType = link.dataset.type;
        const linkUuid = getLinkUuid(link);
        if (linkUuid === null)
            return;
        link.classList.remove('is-selected');
        link.removeAttribute('aria-current');
        removeFromSelectedFilters(linkType, linkUuid);
        updateLink(searchLink);
        updateBadges(el);
        updateActiveFilters(el, searchLink, wrapperEl);
        if (wrapperEl)
            refreshCount(searchLink, wrapperEl);
    };
}
// Restore wrapper to its default state: label + hidden count span
function resetWrapper(wrapperEl) {
    const label = wrapperEl.dataset.label ?? '';
    wrapperEl.innerHTML = label;
}
function refreshCount(searchLink, wrapperEl) {
    if (hasActiveFilters()) {
        updateCount(searchLink, wrapperEl);
    }
    else {
        resetWrapper(wrapperEl);
    }
}
// Build the /filters/count endpoint URL from the search link base path + current selected filters
function buildCountUrl(link) {
    const linkBase = link.dataset.linkBase;
    const locale = linkBase.split('/')[1] || window.location.pathname.split('/')[1];
    let url = `/${locale}/filters/count?path=${linkBase}`;
    Object.keys(selectedFilters).forEach((type) => {
        selectedFilters[type].forEach((uuid) => {
            url += `&${type}[]=${uuid}`;
        });
    });
    return url;
}
let countAbortController = null;
async function fetchResultsCount(url) {
    if (countAbortController) {
        countAbortController.abort();
    }
    countAbortController = new AbortController();
    const response = await fetch(url, {
        signal: countAbortController.signal,
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json',
        },
    });
    if (!response.ok)
        return null;
    const data = await response.json();
    if (typeof data?.variantsCount !== 'number')
        return null;
    return data.variantsCount;
}
async function updateCount(searchLink, wrapperEl) {
    // Loader replaces entire wrapper content during fetch
    wrapperEl.innerHTML = '<span class="loader"></span>';
    try {
        const count = await fetchResultsCount(buildCountUrl(searchLink));
        const label = wrapperEl.dataset.label ?? '';
        if (!hasActiveFilters() || count === null) {
            resetWrapper(wrapperEl);
            return;
        }
        wrapperEl.innerHTML = `${label} <span class="filters__count"> - ${count}</span>`;
    }
    catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
            resetWrapper(wrapperEl);
        }
    }
}
function updateLink(link) {
    let linkBase = link.dataset.linkBase;
    const linkTypes = Object.keys(selectedFilters);
    linkTypes.forEach((type) => {
        selectedFilters[type].forEach((uuid) => {
            linkBase += (linkBase.includes('?') ? '&' : '?') + `${type}[]=${uuid}`;
        });
    });
    link.setAttribute('href', linkBase);
}
export default function addFilters() {
    const filters = document.querySelectorAll('[data-component="filters"]');
    filters.forEach((el) => {
        const links = el.querySelectorAll('[data-filters-target="link"]');
        const searchLink = el.querySelector('[data-filters-target="search"]');
        const wrapperEl = el.querySelector('[data-filters-target="count-wrapper"]');
        if (!searchLink)
            return;
        // Save original label text so we can restore it after each fetch
        if (wrapperEl) {
            const textContent = Array.from(wrapperEl.childNodes)
                .filter((n) => n.nodeType === Node.TEXT_NODE)
                .map((n) => n.textContent)
                .join('')
                .trim();
            wrapperEl.dataset.label = textContent;
        }
        setSelectedFilters(links);
        updateLink(searchLink);
        updateBadges(el);
        updateActiveFilters(el, searchLink, wrapperEl);
        if (wrapperEl && hasActiveFilters()) {
            refreshCount(searchLink, wrapperEl);
        }
        links.forEach((link) => {
            link.addEventListener('click', (e) => {
                selectFilter(e);
                updateLink(searchLink);
                updateBadges(el);
                updateActiveFilters(el, searchLink, wrapperEl);
                if (wrapperEl)
                    refreshCount(searchLink, wrapperEl);
            });
        });
    });
}
